import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../api";
import DatePicker from "../common/DatePicker";
import Button from "../common/Button";
import { TimePicker15Min } from "../common/TimePicker15Min";
import { useCurrentUser } from "../../context/UserContext";
import { showConfirm } from '../../utils/popup';
import { parseSessionDays } from "../../utils/sessionDays";
import { formatTimeFromHhMm } from "../../utils/regionalFormat";
import { parseDateInput, toRegionalDateFromISO, getDateFormatPlaceholder } from "../../utils/date";
import { userDisplayName } from '../../utils/displayNames';

const WEEKDAY_VALUES = [1, 2, 3, 4, 5, 6, 7];

export default function SessionForm({
    role = "MANAGER",
    initialBatchId = "",
    initial = null,
    batchIdFixed = null,
    onCancel = null,
    onSubmit = null
}) {
    const { t } = useTranslation();
    const { user } = useCurrentUser();
    const basePath =
        role === "ADMIN" ? "/admin" : role === "MANAGER" ? "/manager" : "/coach";

    const [batches, setBatches] = useState([]);
    const [coaches, setCoaches] = useState([]);
    const [saving, setSaving] = useState(false);
    const [optionsLoadError, setOptionsLoadError] = useState(false);

    const isEditMode = !!initial;

    const [form, setForm] = useState({
        batchId: "",
        coachId: "",
        startTime: "",
        endTime: "",
    });

    const [selectedDays, setSelectedDays] = useState(new Set());
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const parseRegionalDate = parseDateInput;

    // Initialize form with initial data (for edit mode)
    useEffect(() => {
        if (initial) {
            const extractDateTime = (isoString) => {
                if (!isoString) return { date: "", time: "" };
                try {
                    const dt = new Date(isoString);
                    if (Number.isNaN(dt.getTime())) return { date: "", time: "" };
                    // Use local calendar parts so edit doesn't shift the day across timezones.
                    const y = dt.getFullYear();
                    const m = String(dt.getMonth() + 1).padStart(2, "0");
                    const d = String(dt.getDate()).padStart(2, "0");
                    const time = `${String(dt.getHours()).padStart(2, "0")}:${String(dt.getMinutes()).padStart(2, "0")}`;
                    return { date: `${y}-${m}-${d}`, time };
                } catch {
                    return { date: "", time: "" };
                }
            };

            const start = extractDateTime(initial.startTime);
            const end = extractDateTime(initial.endTime);

            setForm({
                batchId: String(initial.batchId || batchIdFixed || ""),
                coachId: String(initial.coachId || ""),
                startTime: start.time,
                endTime: end.time,
            });

            // For edit mode, set the date fields
            if (start.date) {
                const regional = toRegionalDateFromISO(start.date);
                setFromDate(regional);
                setToDate(regional);
            }
        } else if (batchIdFixed) {
            setForm((f) => ({
                ...f,
                batchId: String(batchIdFixed),
            }));
        } else if (initialBatchId) {
            setForm((f) => ({
                ...f,
                batchId: String(initialBatchId),
            }));
        }
    }, [initial, initialBatchId, batchIdFixed]);

    useEffect(() => {
        if (initialBatchId && !initial) {
            setForm((f) => ({
                ...f,
                batchId: String(initialBatchId),
            }));
        }
    }, [initialBatchId, initial]);

    useEffect(() => {
        let inherited = initialBatchId;
        if (!inherited) {
            const q = new URLSearchParams(window.location.search).get("batchId");
            if (q) inherited = q;
        }
        if (inherited) {
            setForm(f => ({ ...f, batchId: String(f.batchId || inherited) }));
        }
    }, [initialBatchId]);



    useEffect(() => {
        const batch = batches.find(
            (b) => String(b.id ?? b.batchId) === String(form.batchId)
        );
        if (!batch) return;

        // Edit mode: keep the session's saved times/coach; only apply batch defaults on create.
        if (isEditMode) {
            if (batch.sessionDays && selectedDays.size === 0) {
                setSelectedDays(new Set(parseSessionDays(batch.sessionDays)));
            }
            return;
        }

        // For coaches, don't override their coachId
        const coachIdToUse = (role === "COACH" && user?.id)
            ? String(user.id)
            : (batch.coachId ? String(batch.coachId) : form.coachId);

        setForm((f) => ({
            ...f,
            coachId: coachIdToUse,
            startTime: batch.defaultStartTod ?? f.startTime,
            endTime: batch.defaultEndTod ?? f.endTime,
        }));

        if (batch.sessionDays) {
            setSelectedDays(new Set(parseSessionDays(batch.sessionDays)));
        }
    }, [form.batchId, batches, isEditMode, role, user?.id]);



    useEffect(() => {
        (async () => {
            try {
                const r = await API.get(`${basePath}/batches`);
                setBatches(r?.data?.content ?? r?.data ?? []);
            } catch {
                setBatches([]);
                setOptionsLoadError(true);
            }
        })();

        (async () => {
            // For COACH role, use current user instead of fetching all coaches (403 error)
            if (role === "COACH" && user) {
                setCoaches([user]);
                setForm(f => ({ ...f, coachId: String(user.id) }));
            } else {
                // For ADMIN/MANAGER, fetch all coaches
                try {
                    const r = await API.get(
                        `${basePath}/users?role=COACH&active=true&page=0&size=200`
                    );
                    const coachesList = r?.data?.content ?? r?.data ?? [];
                    setCoaches(coachesList);
                } catch {
                    setCoaches([]);
                    setOptionsLoadError(true);
                }
            }
        })();
    }, [basePath, role, user]);

    // Remove the separate auto-select effect as it's now handled above
    // useEffect(() => {
    //     if (role === "COACH" && user?.id && !initial && coaches.length > 0) {
    //         setForm(f => ({ ...f, coachId: String(user.id) }));
    //     }
    // }, [role, user, initial, coaches]);

    const onChange = (e) => {
        const { name, value } = e.target;
        if (name === "batchId") {
            // For coaches, preserve the coachId when batch changes
            if (role === "COACH" && user?.id) {
                setForm({ batchId: value, coachId: String(user.id), startTime: "", endTime: "" });
            } else {
                setForm({ batchId: value, coachId: "", startTime: "", endTime: "" });
            }
            setSelectedDays(new Set());
            setFromDate("");
            setToDate("");
            return;
        }
        setForm(f => ({ ...f, [name]: value }));
    };

    const toggleDay = (v) => {
        setSelectedDays(prev => {
            const s = new Set(prev);
            s.has(v) ? s.delete(v) : s.add(v);
            return s;
        });
    };

    const createPayloads = () => {
        const start = parseRegionalDate(fromDate);
        const end = parseRegionalDate(toDate);
        if (!start || !end) return [];

        const out = [];
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
            const iso = ((d.getDay() + 6) % 7) + 1;
            if (!selectedDays.has(iso)) continue;

            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, "0");
            const dd = String(d.getDate()).padStart(2, "0");

            out.push({
                batchId: Number(form.batchId),
                coachId: Number(form.coachId),
                startTime: `${y}-${m}-${dd}T${form.startTime}`,
                endTime: `${y}-${m}-${dd}T${form.endTime}`,
            });
        }
        return out;
    };

    const weekDays = useMemo(
        () => WEEKDAY_VALUES.map((v) => ({ v, n: t(`days.short.${v}`) })),
        [t]
    );

    const selectedDaysDisplay = useMemo(
        () => weekDays.filter(w => selectedDays.has(w.v)).map(w => w.n).join(", ") || "—",
        [selectedDays, weekDays]
    );

    const calculateDuration = (startTime, endTime) => {
        const [startHour, startMin] = startTime.split(':').map(Number);
        const [endHour, endMin] = endTime.split(':').map(Number);
        const startMinutes = startHour * 60 + startMin;
        const endMinutes = endHour * 60 + endMin;
        return (endMinutes - startMinutes) / 60; // duration in hours
    };

    const formatDuration = (hours) => {
        const h = Math.floor(hours);
        const m = Math.round((hours - h) * 60);
        if (m === 0) return t('sessions.form.hour', { count: h });
        return `${t('sessions.form.hour', { count: h })} ${t('sessions.form.minute', { count: m })}`;
    };

    const onSubmitForm = async (e) => {
        e.preventDefault();
        setSaving(true);

        if (!form.batchId || !form.coachId) {
            alert(t('sessions.form.fillRequired'));
            setSaving(false);
            return;
        }

        // Validate that end time is after start time
        if (form.endTime && form.startTime && form.endTime <= form.startTime) {
            alert(t('sessions.form.endAfterStart'));
            setSaving(false);
            return;
        }

        // Check duration and confirm if > 3 hours
        if (form.startTime && form.endTime) {
            const duration = calculateDuration(form.startTime, form.endTime);
            if (duration > 3) {
                const sessionDate = isEditMode ? fromDate : `${fromDate} to ${toDate}`;
                const confirmed = await showConfirm(
                    t('sessions.form.longSessionWarning', {
                        dates: sessionDate,
                        start: formatTimeFromHhMm(form.startTime),
                        end: formatTimeFromHhMm(form.endTime),
                        duration: formatDuration(duration),
                    })
                );
                if (!confirmed) {
                    setSaving(false);
                    return;
                }
            }
        }

        try {
            if (isEditMode) {
                // Edit mode: single session update
                const start = parseRegionalDate(fromDate);
                if (!start) {
                    alert(t('sessions.form.invalidDate'));
                    setSaving(false);
                    return;
                }

                const y = start.getFullYear();
                const m = String(start.getMonth() + 1).padStart(2, "0");
                const dd = String(start.getDate()).padStart(2, "0");

                const payload = {
                    batchId: Number(form.batchId),
                    coachId: Number(form.coachId),
                    startTime: `${y}-${m}-${dd}T${form.startTime}`,
                    endTime: `${y}-${m}-${dd}T${form.endTime}`,
                };

                if (onSubmit) {
                    await onSubmit(payload, null);
                } else {
                    await API.put(`${basePath}/sessions/${initial.sessionId}`, payload);
                    alert(t('sessions.form.updated'));
                }
            } else {
                // Create mode: recurring sessions
                if (selectedDays.size === 0) {
                    alert(t('sessions.form.selectDay'));
                    setSaving(false);
                    return;
                }

                const payloads = createPayloads();
                if (!payloads.length) {
                    alert(t('sessions.form.invalidDateRange'));
                    setSaving(false);
                    return;
                }

                if (onSubmit) {
                    // Pass the filtered payloads array to custom onSubmit
                    await onSubmit(payloads, null);
                } else {
                    // Default behavior: create all sessions
                    for (const p of payloads) {
                        await API.post(`${basePath}/sessions`, p);
                    }
                    alert(t('sessions.form.created', { count: payloads.length }));
                }
            }
        } catch (err) {
            console.error(err);
            const fallback = isEditMode ? t('sessions.form.updateFailed') : t('sessions.form.createFailed');
            alert(apiErrorMessage(err, fallback));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header with gradient */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-3 flex items-center justify-between">
                <div>
                    <h2 className="text-xl font-bold text-white flex items-center gap-2">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {isEditMode ? t('sessions.form.editTitle') : t('sessions.form.addTitle')}
                    </h2>
                    <p className="text-blue-100 text-xs mt-0.5">
                        {isEditMode ? t('sessions.form.editSubtitle') : t('sessions.form.addSubtitle')}
                    </p>
                </div>
                {onCancel && (
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
                        title={t('sessions.form.close')}
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            <form onSubmit={onSubmitForm} className="p-5 space-y-4 overflow-y-auto">
                {optionsLoadError && (
                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800" role="status">
                        {t('common.optionsLoadFailed')}
                    </div>
                )}
                {/* Batch + Coach */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                            {t('sessions.form.trainingBatch')}
                        </label>
                        <select
                            name="batchId"
                            value={form.batchId}
                            onChange={onChange}
                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white disabled:bg-gray-100"
                            required
                            disabled={optionsLoadError || (isEditMode && batchIdFixed)}
                        >
                            <option value="">{t('sessions.form.selectBatch')}</option>
                            {batches.map(b => (
                                <option key={b.id} value={b.id}>{b.name}</option>
                            ))}
                        </select>
                    </div>

                    <div>
                        <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 mb-1.5">
                            <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                            {t('sessions.form.sessionCoach')}
                        </label>
                        <select
                            name="coachId"
                            value={form.coachId}
                            onChange={onChange}
                            className="w-full px-3 py-2 text-sm rounded-lg border-2 border-gray-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition-all bg-white disabled:bg-gray-100 disabled:cursor-not-allowed"
                            required
                            disabled={optionsLoadError || role === "COACH"}
                        >
                            <option value="">{t('sessions.form.selectCoach')}</option>
                            {coaches.map(c => {
                                const nameParts = [c.firstName, c.middleName, c.lastName].filter(Boolean);
                                const fullName = nameParts.length > 0 ? nameParts.join(" ") : c.username || userDisplayName(null, c.id, t);
                                return (
                                    <option key={c.id} value={c.id}>
                                        {fullName} (#{c.id})
                                    </option>
                                );
                            })}
                        </select>
                        {role === "COACH" && (
                            <p className="mt-1 text-xs text-blue-600 flex items-center gap-1">
                                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                </svg>
                                {t('sessions.form.autoSelected')}
                            </p>
                        )}
                    </div>
                </div>

                {/* Times with visual divider */}
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-4 border border-blue-100">
                    <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('sessions.form.sessionTiming')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <TimePicker15Min
                                value={form.startTime}
                                onChange={(val) => setForm(f => ({
                                    ...f,
                                    startTime: val,
                                    endTime: f.endTime && val && f.endTime <= val ? "" : f.endTime,
                                }))}
                                placeholder={t('sessions.form.selectStartTime')}
                                label={t('sessions.form.startTime')}
                                className="w-full"
                                required
                                showDuration={true}
                                otherTime={form.endTime}
                                maxTime={form.endTime || null}
                            />
                        </div>

                        <div>
                            <TimePicker15Min
                                value={form.endTime}
                                onChange={(val) => setForm(f => ({ ...f, endTime: val }))}
                                placeholder={t('sessions.form.selectEndTime')}
                                label={t('sessions.form.endTime')}
                                className="w-full"
                                required
                                showDuration={true}
                                otherTime={form.startTime}
                                minTime={form.startTime || null}
                            />
                        </div>
                    </div>
                </div>

                {/* Date range */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 border border-purple-100">
                    <h3 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1.5">
                        <svg className="w-4 h-4 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        {isEditMode ? t('sessions.form.sessionDate') : t('sessions.form.dateRange')}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                {isEditMode
                                    ? t('sessions.form.date', { format: getDateFormatPlaceholder() })
                                    : t('sessions.form.from', { format: getDateFormatPlaceholder() })}
                            </label>
                            <DatePicker value={fromDate} onChange={setFromDate} className="w-full" />
                        </div>
                        {!isEditMode && (
                            <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1.5">
                                    {t('sessions.form.to', { format: getDateFormatPlaceholder() })}
                                </label>
                                <DatePicker value={toDate} onChange={setToDate} className="w-full" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Days - only show in create mode */}
                {!isEditMode && (
                    <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-4 border border-green-100">
                        <h3 className="text-xs font-semibold text-gray-700 mb-2.5 flex items-center gap-1.5">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                            </svg>
                            {t('sessions.form.selectDays')}
                        </h3>
                        <div className="flex flex-wrap gap-2">
                            {weekDays.map(d => (
                                <button
                                    key={d.v}
                                    type="button"
                                    onClick={() => toggleDay(d.v)}
                                    className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${selectedDays.has(d.v)
                                        ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-300"
                                        : "bg-white text-gray-600 border-2 border-gray-200 hover:border-blue-400"
                                        }`}
                                >
                                    {d.n}
                                </button>
                            ))}
                        </div>
                        {selectedDays.size > 0 && (
                            <div className="mt-2.5 px-3 py-1.5 bg-blue-100 rounded-lg border border-blue-200">
                                <span className="text-xs font-medium text-blue-800">{t('sessions.form.selected')} </span>
                                <span className="text-xs text-blue-700">{selectedDaysDisplay}</span>
                            </div>
                        )}
                    </div>
                )}

                {/* Action buttons */}
                <div className="flex justify-end gap-3 pt-3 border-t border-gray-200">
                    {onCancel && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-5 py-2 rounded-lg bg-gray-100 text-gray-700 text-sm font-medium hover:bg-gray-200 transition-colors border border-gray-300"
                        >
                            {t('common.cancel')}
                        </button>
                    )}
                    <button
                        type="submit"
                        disabled={saving}
                        className="px-6 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                        {saving ? (
                            <>
                                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                {isEditMode ? t('sessions.form.updating') : t('sessions.form.creating')}
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                {isEditMode ? t('sessions.form.update') : t('sessions.form.create')}
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
}
